import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { useTranslation } from '@lib/i18n/useTranslation'
import { cn } from '@lib/utils/helpers'
import { Tooltip } from '@/components/ui/Tooltip'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ApplyWizard } from '@/features/apply/components/ApplyWizard'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  registerUser,
  selectAuthLoading,
  selectCurrentInviteId,
  selectInviteData,
  clearError,
  setInviteData,
  setCurrentInviteId,
} from '@/features/auth/authSlice'
import { fetchApplications } from '@/features/applicant/applicationSlice'
import type { InviteData } from '@/types/auth'
import { formatDateForInput, unformatPhoneNumber, formatPhoneNumber } from '@/types/application'
import { getStorageItem, getStorageJSON, STORAGE_KEYS } from '@/lib/utils/storage'
import { getInvitationData } from '@/lib/api/inviteService'
import {
  getCoApplicantInviteDetails,
  getInviteDetails,
} from '@/features/apply/services/applyService'
import { validateDateOfBirth } from '@/lib/utils/dateValidation'
import { DatePicker } from '@components/ui/DatePicker'
import { filterPhoneInput } from '@/lib/utils/formatters'
import { strengthBarColors } from '../utils/passwordStrength'
import type { ApplyErrorDialog, VerifyInfoFormData, VerifyInfoSetPasswordProps } from '../types'

/**
 * Validation schema for form
 */
/**
 * Angular `signup.fromgroup.ts:9-17` — no leading/trailing space, at most three
 * space-separated tokens, restricted punctuation set.
 */
const NAME_PATTERN =
  /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+){0,2}(?<! )$/

const verifyInfoSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .regex(NAME_PATTERN, 'Enter Valid First Name.'),
    middleName: z
      .string()
      .refine((val) => !val || NAME_PATTERN.test(val), 'Enter Valid Middle Name.'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .regex(NAME_PATTERN, 'Enter Valid Last Name.'),
    dob: z.string().superRefine((value, ctx) => {
      // DOB is optional — only validate when a value is provided
      if (!value || !value.trim()) return
      const result = validateDateOfBirth(value)
      if (!result.isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.error || 'Please enter a valid date of birth',
        })
      }
    }),
    email: z
      .string()
      .min(1, 'Email address is required')
      .email('Please enter a valid email address'),
    confirmEmail: z.string().min(1, 'Please confirm your email address'),
    mobilePhone: z
      .string()
      .min(1, 'Phone number is required')
      // Angular mobileValidator: /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/ on
      // digits only — an optional 1-2 digit country code plus 10 digits.
      .refine((val) => {
        const digits = val.replace(/\D/g, '')
        return digits.length >= 10 && digits.length <= 12
      }, 'Enter Valid Phone Number. Should match pattern [+X (XXX) XXX-XXXX].'),
    isSharedPhone: z.boolean(),
    password: z
      .string()
      .min(1, 'Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.email === data.confirmEmail, {
    message: 'Email addresses do not match',
    path: ['confirmEmail'],
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/**
 * Angular: `location.href.replace(this.inviteId, response.new_invite_code)` — swaps the
 * code in place so `/co-applicant/apply/…` keeps its prefix (register.component.ts:161).
 */
function redirectToNewInviteCode(currentCode: string, newCode: string): void {
  window.location.href = window.location.href.replace(currentCode, newCode)
}

/**
 * Map invitation data to form data
 */
function mapInvitationDataToForm(invitationData: InviteData): Partial<VerifyInfoFormData> {
  // Format phone number if it exists
  const formattedPhone = invitationData.mobile_no 
    ? formatPhoneNumber(invitationData.mobile_no) 
    : ''
  
  return {
    firstName: invitationData.first_name || '',
    middleName: invitationData.middle_name || '',
    lastName: invitationData.last_name || '',
    dob: invitationData.birthdate ? formatDateForInput(invitationData.birthdate) : '',
    email: invitationData.email || '',
    confirmEmail: invitationData.email || '',
    mobilePhone: formattedPhone,
    isSharedPhone: false,
  }
}

export function VerifyInfoSetPassword({
  isCoApplicant = false,
}: VerifyInfoSetPasswordProps = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  // `/apply/:inviteId` (Apply Now) — keep path; reuse this UI without numbered steps
  const { inviteId: routeInviteId } = useParams<{ inviteId?: string }>()
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errorDialog, setErrorDialog] = useState<ApplyErrorDialog | null>(null)
  // URL-supplied code resolved (or typed via the in-place wizard).
  const [inviteResolved, setInviteResolved] = useState(false)
  const [passwordReadonly, setPasswordReadonly] = useState(true)
  const [confirmPasswordReadonly, setConfirmPasswordReadonly] = useState(true)
  const inviteDetailsFetchedRef = useRef(false)
  const autofillScrubbedRef = useRef(false)

  // Redux state
  const isLoading = useAppSelector(selectAuthLoading)
  const reduxInviteData = useAppSelector(selectInviteData)
  const reduxInviteCode = useAppSelector(selectCurrentInviteId)

  // Load invitation data from localStorage or Redux
  const invitationData = useMemo(() => {
    // First check Redux state
    if (reduxInviteData) {
      return reduxInviteData
    }
    
    // Fall back to localStorage
    return getStorageJSON<InviteData>(STORAGE_KEYS.invitationData)
  }, [reduxInviteData])

  // Prefer invite code from `/apply/:inviteId` URL, then Redux / storage
  const inviteCode = useMemo(() => {
    return (
      routeInviteId?.trim() ||
      reduxInviteCode ||
      getStorageItem(STORAGE_KEYS.inviteCode) ||
      ''
    )
  }, [routeInviteId, reduxInviteCode])


  // Get default values from invitation data
  const defaultValues = useMemo(() => {
    const baseDefaults: Partial<VerifyInfoFormData> = {
      isSharedPhone: false,
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      email: '',
      confirmEmail: '',
      mobilePhone: '',
      password: '',
      confirmPassword: '',
    }

    if (invitationData) {
      const mappedData = mapInvitationDataToForm(invitationData)
      return { ...baseDefaults, ...mappedData }
    }

    return baseDefaults
  }, [invitationData])

  // Track which fields are autofilled from invitation data
  const autoFilledFields = useMemo(() => {
    const fields = new Set<string>()
    
    if (invitationData) {
      if (invitationData.first_name) fields.add('firstName')
      if (invitationData.last_name) fields.add('lastName')
      if (invitationData.email) {
        fields.add('email')
        fields.add('confirmEmail')
      }
      if (invitationData.mobile_no) fields.add('mobilePhone')
    }
    
    return fields
  }, [invitationData])

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VerifyInfoFormData>({
    resolver: zodResolver(verifyInfoSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as VerifyInfoFormData,
  })

  // Reset form when invitation data is loaded (Angular: signupForm.patchValue(response))
  useEffect(() => {
    if (!invitationData) return
    if (!invitationData.first_name && !invitationData.email && !invitationData.mobile_no) {
      return
    }
    reset({
      isSharedPhone: false,
      firstName: invitationData.first_name || '',
      middleName: invitationData.middle_name || '',
      lastName: invitationData.last_name || '',
      dob: invitationData.birthdate
        ? formatDateForInput(invitationData.birthdate)
        : '',
      email: invitationData.email || '',
      confirmEmail: invitationData.email || '',
      mobilePhone: invitationData.mobile_no
        ? formatPhoneNumber(invitationData.mobile_no)
        : '',
      password: '',
      confirmPassword: '',
    })
  }, [invitationData, reset])

  // Browser often autofills email into DOB and saved password into Password — clear once on load
  useEffect(() => {
    if (autofillScrubbedRef.current) return
    const timer = window.setTimeout(() => {
      autofillScrubbedRef.current = true
      const dobValue = getValues('dob') || ''
      // Email wrongly injected into DOB (contains @)
      if (dobValue.includes('@')) {
        setValue('dob', '', { shouldValidate: false, shouldDirty: false })
      }
      // Clear browser-saved password autofill (user must set a new password)
      setValue('password', '', { shouldValidate: false, shouldDirty: false })
      setValue('confirmPassword', '', { shouldValidate: false, shouldDirty: false })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [getValues, setValue, invitationData])

  // Clear errors on mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const password = useWatch({ control, name: 'password' })
  const dob = useWatch({ control, name: 'dob' })
  const mobilePhone = useWatch({ control, name: 'mobilePhone' })

  const strengthColors = useMemo(
    () => strengthBarColors(password || ''),
    [password],
  )

  const onSubmit = async (data: VerifyInfoFormData) => {
    setErrorDialog(null)
    dispatch(clearError())

    // Check if invite code exists
    if (!inviteCode) {
      setErrorDialog({
        title: 'Validation Message',
        message: 'Invite code is missing. Please go back and enter your invite code.',
        primaryLabel: 'Ok',
        onPrimary: () => setErrorDialog(null),
      })
      return
    }

    try {
      // Format data for API
      const registrationData = {
        email: data.email.trim().toLowerCase(),
        confirm_email: data.confirmEmail.trim().toLowerCase(),
        first_name: data.firstName.trim(),
        middle_name: data.middleName?.trim() || '',
        last_name: data.lastName.trim(),
        mobile_no: unformatPhoneNumber(data.mobilePhone),
        shared_phone_no: data.isSharedPhone ? '1' : '0',
        password: data.password,
        confirm_password: data.confirmPassword,
        invite_code: inviteCode,
        // Angular sends YYYY-MM-DD (payment.datahandler `formatDate`); DatePicker
        // already holds that format, so pass it through rather than reformatting.
        birthdate: data.dob?.trim() ? data.dob.trim() : undefined,
        // Angular register.component.ts:312 — marks the registration as web-originated.
        is_web: true,
      }

      // Call register and auto-login
      const result = await dispatch(registerUser(registrationData)).unwrap()

      // On success, fetch resident application data
      if (result && result.token) {
        try {
          await dispatch(fetchApplications()).unwrap()
        } catch {
          // Continue to dashboard even if fetch fails
          // Error is already handled by Redux slice
        }

        // Navigate to applicant dashboard
        navigate('/applicant/application/about')
      } else if (result) {
        // Try to navigate anyway if we got a result (even without token)
        navigate('/applicant/application/about')
      }
    } catch (error) {
      // Error from Redux thunk rejection — Angular notify(msg, 0) = "Validation Message" popup
      const errorMessage = typeof error === 'string' ? error : 'Registration failed. Please try again.'
      setErrorDialog({
        title: 'Validation Message',
        message: errorMessage,
        primaryLabel: 'Ok',
        onPrimary: () => setErrorDialog(null),
      })
    }
  }

  /**
   * Angular register.component.ts `getInvitationData` error branches:
   * `error.email` → Sign In prefilled; invite-used / already-registered → Sign In; otherwise notify().
   */
  const openInviteErrorDialog = useCallback((err: unknown) => {
    const detail = (err ?? {}) as {
      message?: string
      email?: string
      type?: string
      newInviteCode?: string
    }
    const message = detail.message || 'Failed to load invite details'

    // Expired code — Angular replaces the code in the URL and reloads the page.
    if (detail.newInviteCode) {
      setErrorDialog({
        title: 'Invite Code Expired',
        message: `We found New matching Invite code ${detail.newInviteCode}, Use this invite code`,
        primaryLabel: 'Yes, Use This',
        onPrimary: () => {
          redirectToNewInviteCode(routeInviteId ?? '', detail.newInviteCode as string)
        },
      })
      return
    }

    if (detail.email) {
      setErrorDialog({
        title: 'Validation Message',
        message,
        primaryLabel: 'Sign In',
        onPrimary: () =>
          navigate(`/login?email=${encodeURIComponent(detail.email as string)}`),
      })
      return
    }
    if (detail.type === 'INVITE_ALREADY_USED') {
      setErrorDialog({
        title: 'Invitation Error',
        message,
        primaryLabel: 'Sign In',
        onPrimary: () => navigate('/login'),
      })
      return
    }
    if (detail.type === 'USER_ALREADY_REGISTERED') {
      setErrorDialog({
        title: 'Validation Message',
        message,
        primaryLabel: 'Sign In',
        onPrimary: () => navigate('/login'),
      })
      return
    }
    // Angular's error handler never reassigns `selectedTab`, so the user simply
    // stays on wizard step 1 behind this dialog (register.component.ts:190-232).
    setErrorDialog({
      title: 'Validation Message',
      message,
      primaryLabel: 'Ok',
      onPrimary: () => setErrorDialog(null),
    })
  }, [navigate, routeInviteId])

  // Redirect if no invite data (only for apply-now wizard — not `/apply/:inviteId`)
  useEffect(() => {
    if (routeInviteId) return
    if (!invitationData && !inviteCode) {
      const timeout = setTimeout(() => {
        if (!invitationData && !inviteCode) {
          navigate('/apply-now/enter-invite-code')
        }
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [invitationData, inviteCode, navigate, routeInviteId])

  // Load invite from URL — Angular getInvitationData parity (GET /invite/:id?page=register)
  // Important: do NOT cancel/skip when Redux invite id updates (that was clearing prefill).
  useEffect(() => {
    const codeFromUrl = routeInviteId?.trim()
    if (!codeFromUrl) {
      return
    }

    let cancelled = false
    inviteDetailsFetchedRef.current = true
    dispatch(setCurrentInviteId(codeFromUrl))

    ;(async () => {
      setErrorDialog(null)
      try {
        // Same public endpoint Angular uses for /apply/:inviteId
        // Angular: `params.coapplicant_inviteId` → `/coapplicant_invite/:id`, and
        // `onInviteSubmit` also branches on a `CO-` prefix (register.component.ts:147).
        const useCoApplicant = isCoApplicant || codeFromUrl.toUpperCase().startsWith('CO-')
        const details = useCoApplicant
          ? await getCoApplicantInviteDetails(codeFromUrl)
          : await getInviteDetails(codeFromUrl)
        if (cancelled) return

        if (details.new_invite_code) {
          redirectToNewInviteCode(codeFromUrl, details.new_invite_code)
          return
        }

        dispatch(setInviteData(details as InviteData))
        setInviteResolved(true)
      } catch (err: unknown) {
        if (cancelled) return
        // Terminal errors (already registered / invite used) — popup now, no retry.
        const detail = (err ?? {}) as { email?: string; type?: string }
        if (detail.email || detail.type) {
          openInviteErrorDialog(err)
          return
        }
        // Fallback to shared invite lookup (also tries co-applicant endpoint)
        try {
          const response = await getInvitationData(codeFromUrl)
          if (cancelled) return
          if (response.success && response.data) {
            const data = response.data as InviteData & { new_invite_code?: string }
            if (data.new_invite_code) {
              redirectToNewInviteCode(codeFromUrl, data.new_invite_code)
              return
            }
            dispatch(setInviteData(data))
            setInviteResolved(true)
            return
          }
        } catch {
          /* use primary error below */
        }

        openInviteErrorDialog(err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [routeInviteId, dispatch, openInviteErrorDialog, isCoApplicant])

  // apply-now path: hydrate from storage invite code when Redux has no details yet
  useEffect(() => {
    if (routeInviteId) return
    if (!inviteCode || invitationData?.first_name) return
    if (inviteDetailsFetchedRef.current) return

    inviteDetailsFetchedRef.current = true
    let cancelled = false
    ;(async () => {
      try {
        const response = await getInvitationData(inviteCode)
        if (!cancelled && response.success && response.data) {
          dispatch(setInviteData(response.data as InviteData))
        }
      } catch {
        /* keep empty form */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [routeInviteId, inviteCode, invitationData, dispatch])

  const renderErrorDialog = () =>
    errorDialog ? (
      <OnScreenModal
        isOpen
        title={errorDialog.title}
        maxWidth="max-w-md"
        onClose={() =>
          errorDialog.onDismiss ? errorDialog.onDismiss() : setErrorDialog(null)
        }
        panelDataTestId="apply-error-dialog"
        footer={
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={errorDialog.onPrimary}
              className="min-w-[9rem] rounded-md bg-teal-dark px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-darker"
              data-testid="apply-error-dialog-primary"
            >
              {errorDialog.primaryLabel}
            </button>
          </div>
        }
      >
        <div className="px-1 py-5 text-center">
          <p className="text-sm leading-relaxed text-slate-700">
            {errorDialog.message}
          </p>
        </div>
      </OnScreenModal>
    ) : null

  /**
   * Angular keeps `selectedTab` on `haveappliedinfo` until the invite resolves and
   * never reassigns it on error (register.component.ts:190-232) — so a bad code
   * leaves the user in the wizard at the same URL. Mirror that: render steps 1-4
   * in place while the code is unresolved, and swap to this form on success.
   */
  if (routeInviteId && !inviteResolved) {
    return (
      <>
        <ApplyWizard
          onInviteVerified={(code, data) => {
            dispatch(setCurrentInviteId(code))
            dispatch(setInviteData(data))
            setInviteResolved(true)
          }}
        />
        {renderErrorDialog()}
      </>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex min-h-screen items-start justify-center px-3 py-6 pb-16 sm:px-4 sm:py-10 sm:pb-20">
        <div className="w-full max-w-5xl rounded-xl bg-white px-5 py-6 shadow-xl sm:p-8 md:p-10">
          {/* Info Banner */}
          <div className="mb-6 rounded-lg border border-teal-100 bg-teal-50 p-4 sm:mb-8 sm:p-5">
            <h2 className="mb-3 text-base font-bold text-gray-900 sm:text-lg">
              {t('verifyInfo.banner.title')}
            </h2>
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              {t('verifyInfo.banner.description')}
            </p>
            <div className="space-y-2 text-sm">
              <ul className="ml-4 list-inside list-disc space-y-1 text-gray-500">
                <li>{t('verifyInfo.banner.step1')}</li>
                <li>{t('verifyInfo.banner.step2')}</li>
                <li>{t('verifyInfo.banner.step3')}</li>
              </ul>
              <p className="mt-3 text-gray-600">
                <strong className="font-semibold text-gray-700">
                  {t('verifyInfo.banner.eligibleTitle')}
                </strong>{' '}
                {t('verifyInfo.banner.eligibleDescription')}
              </p>
              <ul className="ml-4 list-inside list-disc space-y-1 text-gray-500">
                <li>{t('verifyInfo.banner.eligible1')}</li>
                <li>{t('verifyInfo.banner.eligible2')}</li>
              </ul>
            </div>
          </div>

          {/* Title */}
          <h1 className="mb-2 text-center text-2xl font-bold text-teal-dark sm:text-3xl">
            {t('verifyInfo.title')}
          </h1>

          {/* Property Info */}
          <p className="mb-2 text-center text-sm text-gray-700 sm:text-base">
            {t('verifyInfo.propertyInfo')}{' '}
            <strong className="font-semibold text-gray-900">
              {invitationData?.property_name || 'Your Property'}
            </strong>
            .
          </p>
          <div className="mx-auto mb-6 max-w-3xl space-y-1 text-center text-sm leading-relaxed text-gray-500 sm:mb-8">
            <p>{t('verifyInfo.contactNote')}</p>
            <p>{t('verifyInfo.confirmNote')}</p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
            autoComplete="off"
          >
            {/* Personal Information */}
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-5 sm:p-6">
              <h2 className="mb-4 border-b border-gray-200 pb-3 text-base font-bold text-teal-dark sm:text-lg">
                {t('verifyInfo.sections.personalInfo')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.firstName')}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    {...register('firstName')}
                    // readOnly, not disabled: the global `input:disabled` rule forces
                    // an unreadable grey (globals.css) and disabled fields don't submit.
                    readOnly={autoFilledFields.has('firstName')}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      errors.firstName ? 'border-red-500' : 'border-gray-300',
                      autoFilledFields.has('firstName') &&
                        'bg-gray-100 text-gray-700 cursor-default',
                      isLoading && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="middleName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.middleName')}
                  </label>
                  <input
                    id="middleName"
                    type="text"
                    {...register('middleName')}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      isLoading && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                </div>

                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.lastName')}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    {...register('lastName')}
                    readOnly={autoFilledFields.has('lastName')}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      errors.lastName ? 'border-red-500' : 'border-gray-300',
                      autoFilledFields.has('lastName') &&
                        'bg-gray-100 text-gray-700 cursor-default',
                      isLoading && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <p className="mt-4 rounded-md border border-gray-200 bg-white p-3 text-sm leading-relaxed text-gray-600">
                <strong className="font-bold text-gray-800">
                  {t('verifyInfo.legalNameNoteLabel')}
                </strong>{' '}
                {t('verifyInfo.legalNameNote')}
              </p>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                <label
                  htmlFor="inputbirthdate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('verifyInfo.fields.dob')}
                </label>
                <DatePicker
                  id="inputbirthdate"
                  name="birthdate"
                  autoComplete="bday"
                  value={dob || ''}
                  onChange={(value) => {
                    setValue('dob', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }}
                  onBlur={() => {
                    void trigger('dob')
                  }}
                  showHelperText={!errors.dob}
                  disabled={isLoading}
                  error={errors.dob?.message}
                  dataTestId="verify-info-dob"
                  placeholder="MM/DD/YYYY"
                />
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-5 sm:p-6">
              <h2 className="mb-4 border-b border-gray-200 pb-3 text-base font-bold text-teal-dark sm:text-lg">
                {t('verifyInfo.sections.contactInfo')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="inputEmail"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.email')}
                    {!invitationData?.email && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    id="inputEmail"
                    type="text"
                    autoComplete="nope"
                    {...register('email')}
                    readOnly={Boolean(invitationData?.email)}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      errors.email ? 'border-red-500' : 'border-gray-300',
                      (isLoading || invitationData?.email) &&
                        'bg-gray-100 text-gray-700 cursor-default',
                      isLoading && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirm_email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.confirmEmail')}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="confirm_email"
                    type="text"
                    autoComplete="nope"
                    {...register('confirmEmail')}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      errors.confirmEmail ? 'border-red-500' : 'border-gray-300',
                      isLoading && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {errors.confirmEmail && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmEmail.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="inputContact"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.mobilePhone')}
                    {!invitationData?.mobile_no && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    id="inputContact"
                    type="text"
                    autoComplete="nope"
                    name="mobile_no"
                    value={mobilePhone || ''}
                    onChange={(e) => {
                      const filtered = filterPhoneInput(e.target.value)
                      setValue('mobilePhone', filtered, { shouldValidate: true })
                    }}
                    onBlur={() => {
                      void trigger('mobilePhone')
                    }}
                    readOnly={Boolean(invitationData?.mobile_no)}
                    disabled={isLoading}
                    className={cn(
                      'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent',
                      errors.mobilePhone ? 'border-red-500' : 'border-gray-300',
                      (isLoading || invitationData?.mobile_no) &&
                        'bg-gray-100 text-gray-700 cursor-default',
                      isLoading && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {errors.mobilePhone && (
                    <p className="mt-1 text-sm text-red-600">{errors.mobilePhone.message}</p>
                  )}
                  <div className="flex items-center mt-2">
                    <input
                      id="shared_phone_no"
                      type="checkbox"
                      {...register('isSharedPhone')}
                      disabled={isLoading}
                      className="h-4 w-4 rounded border-gray-300 text-teal-dark focus:ring-teal-500"
                    />
                    <label
                      htmlFor="shared_phone_no"
                      className="ml-2 text-sm text-gray-600"
                    >
                      {t('verifyInfo.fields.sharedPhone')}
                    </label>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-gray-600">
                <strong className="font-bold text-gray-800">
                  {t('verifyInfo.privacyNoteLabel')}
                </strong>{' '}
                {t('verifyInfo.privacyNote')}
              </p>
            </section>

            {/* Set Password */}
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-5 sm:p-6">
              <h2 className="mb-4 border-b border-gray-200 pb-3 text-base font-bold text-teal-dark sm:text-lg">
                {t('verifyInfo.sections.setPassword')}
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label
                    htmlFor="inputPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.password')}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="inputPassword"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      readOnly={passwordReadonly}
                      {...register('password')}
                      onFocus={() => setPasswordReadonly(false)}
                      disabled={isLoading}
                      className={cn(
                        'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-12',
                        errors.password ? 'border-red-500' : 'border-gray-300',
                        isLoading && 'opacity-50 cursor-not-allowed'
                      )}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Tooltip
                        content={showPassword ? 'Hide password' : 'Show password'}
                        side="top"
                      >
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <Eye className="h-5 w-5" />
                          ) : (
                            <EyeOff className="h-5 w-5" />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="inputConfirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('verifyInfo.fields.confirmPassword')}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="inputConfirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      readOnly={confirmPasswordReadonly}
                      {...register('confirmPassword')}
                      onFocus={() => setConfirmPasswordReadonly(false)}
                      disabled={isLoading}
                      className={cn(
                        'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-12',
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300',
                        isLoading && 'opacity-50 cursor-not-allowed'
                      )}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Tooltip
                        content={
                          showConfirmPassword ? 'Hide password' : 'Show password'
                        }
                        side="top"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="text-gray-500 hover:text-gray-700"
                          aria-label={
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showConfirmPassword ? (
                            <Eye className="h-5 w-5" />
                          ) : (
                            <EyeOff className="h-5 w-5" />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {/* Password Strength — 5-segment bar (same as Reset Password / Angular) */}
              {password && (
                <div
                  id="strength"
                  className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-600"
                  aria-live="polite"
                >
                  <span>{t('verifyInfo.passwordStrength')}:</span>
                  <ul className="m-0 flex list-none items-center gap-px p-0">
                    {strengthColors.map((color, i) => (
                      <li
                        key={i}
                        className="inline-block h-[5px] w-5 rounded-sm"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Submit Button */}
            <div className="flex flex-col items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                className="w-full max-w-md rounded-md bg-teal-dark px-6 py-3.5 text-base font-bold text-white transition-colors hover:bg-teal-darker disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading || isSubmitting
                  ? t('verifyInfo.submitting')
                  : t('verifyInfo.submit')}
              </button>

              <Link
                to="/login"
                className="text-sm font-medium text-teal-dark hover:text-teal-darker"
              >
                {t('verifyInfo.signInLink')}
              </Link>
            </div>
          </form>

          {/* Footer Note */}
          <div className="mt-6 border-t border-gray-200 pt-5 text-center text-xs leading-relaxed text-gray-500 sm:mt-8 sm:pt-6">
            <p>{t('verifyInfo.emailNote')}</p>
            <a
              href="mailto:notifications@libertyrent.io"
              className="font-medium text-teal-dark hover:text-teal-darker"
            >
              notifications@libertyrent.io
            </a>
          </div>
        </div>
      </div>

      {renderErrorDialog()}
    </div>
  )
}

